import React from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { Box, Typography, Button } from 'omaha-design-system';
import { setLeagueFilter } from '../store/slice';
import { DEFAULT_PROPS_LEAGUE, getPropsLeagueOptions } from '../data/leagueConfig';

interface LeagueOption {
  value: string;
  label: string;
  disabled: boolean;
}

const LEAGUE_OPTIONS: LeagueOption[] = getPropsLeagueOptions();

/**
 * Native-app "no access" screen for a signed-in user without a Props subscription.
 *
 * Only the mobile app renders this: on the web, non-subscribers get the teaser
 * board + upsell card instead (PropsDesktop). In the app, Apple Guideline 3.1.1
 * forbids subscribe/upgrade messaging, purchase links and pricing, so this
 * screen is deliberately neutral — a league switcher and a plain statement.
 */
interface SubscriptionRequiredOverlayProps {
  /** The entitlement check itself failed (network / 5xx): show a neutral retry instead of "no access". */
  checkFailed?: boolean;
  onRetry?: () => void;
}

export const SubscriptionRequiredOverlay: React.FC<SubscriptionRequiredOverlayProps> = ({ checkFailed = false, onRetry }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { league } = useParams<{ league: string }>();
  const selectedLeague = league?.toUpperCase() || DEFAULT_PROPS_LEAGUE;

  const handleLeagueChange = (newLeague: string) => {
    dispatch(setLeagueFilter(newLeague));
    history.push(`/props/${newLeague}`);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#0D0E10',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        zIndex: 1401,
      }}
    >
      {/* Stokastic Logo */}
      <Box
        component="img"
        src={`${process.env.PUBLIC_URL}/images/longLogo.png`}
        alt="Stokastic"
        sx={{
          height: 40,
          width: 'auto',
          mb: 3,
        }}
      />

      {/* League Selector */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          mb: 4,
          p: 0.5,
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '8px',
        }}
      >
        {LEAGUE_OPTIONS.map((option) => (
          <Button
            key={option.value}
            onClick={() => !option.disabled && handleLeagueChange(option.value)}
            disabled={option.disabled}
            sx={{
              minWidth: 60,
              py: 1,
              px: 2,
              borderRadius: '6px',
              fontFamily: '"Instrument Sans", sans-serif',
              fontWeight: 600,
              fontSize: 14,
              textTransform: 'none',
              backgroundColor: selectedLeague === option.value ? '#005CCE' : 'transparent',
              color: selectedLeague === option.value ? 'white' : 'rgba(255, 255, 255, 0.6)',
              '&:hover': {
                backgroundColor: selectedLeague === option.value ? '#0048A8' : 'rgba(255, 255, 255, 0.1)',
              },
              '&.Mui-disabled': {
                color: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            {option.label}
          </Button>
        ))}
      </Box>

      {/* Headline */}
      <Typography
        sx={{
          fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
          fontWeight: 700,
          fontSize: { xs: 28, sm: 36 },
          color: 'white',
          textAlign: 'center',
          mb: 1.5,
        }}
      >
        {checkFailed ? "Couldn't check your access" : `${selectedLeague} Props`}
      </Typography>

      {/* Neutral statement — no subscribe/pricing wording (Apple Guideline 3.1.1) */}
      <Typography
        sx={{
          fontFamily: '"Instrument Sans", sans-serif',
          fontWeight: 400,
          fontSize: 16,
          color: 'rgba(255, 255, 255, 0.6)',
          textAlign: 'center',
          mb: 4,
          maxWidth: 400,
        }}
      >
        {checkFailed ? 'Something went wrong verifying your access. Please try again.' : `You don't have access to ${selectedLeague} Props.`}
      </Typography>

      {checkFailed && onRetry && (
        <Button
          variant="contained"
          onClick={onRetry}
          sx={{
            width: { xs: '100%', sm: 300 },
            maxWidth: 300,
            py: 1.5,
            bgcolor: '#005CCE',
            color: 'white',
            fontFamily: '"Instrument Sans", sans-serif',
            fontWeight: 600,
            fontSize: 16,
            textTransform: 'none',
            borderRadius: '8px',
            '&:hover': { bgcolor: '#0048A8' },
          }}
        >
          Try again
        </Button>
      )}
    </Box>
  );
};

export default SubscriptionRequiredOverlay;
